import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { AlertRow } from "@/components/alerts/AlertRow";
import { ALERT_INSTANCES_LIST_QUERY_KEY } from "@/components/shell/AlertBell";
import { ReadOnlyNotice } from "@/components/ReadOnlyNotice";
import { ApiError, apiFetch } from "@/lib/api";
import type { AlertInstanceResource, Page } from "@/lib/api-types";
import { useIsReadOnly } from "@/lib/current-actor";
import { showToast } from "@/lib/toast-queue";

/**
 * The alert centre (Story 3.9): every currently open `AlertInstance`, as
 * `AlertRow`s, reached from the bell on every screen (EXPERIENCE.md's IA
 * table). Acknowledge and snooze are real, user-initiated calls to
 * `POST /alert-instances/{id}/acknowledge`/`/snooze` — the first time
 * anything but a system actor ever moves one of these rows.
 *
 * The empty state is the frozen sentence, hand-rendered rather than routed
 * through `DataTable`'s own generic empty-state branch (which always
 * supplies a button) — EXPERIENCE.md names this the one sanctioned
 * exception to the mandatory-button rule, and `DataTable` itself never
 * reaches for that exception on its own (VC-3.9-10: no `<button>` anywhere
 * on this state, structurally, not merely visually hidden).
 */

export const EMPTY_STATE_SENTENCE = "Nothing needs your attention today.";

/** Exported for direct, offline testing (VC-3.9-04): the exact rendered
 * copy states repetition has stopped, and explicitly never claims the
 * underlying problem is fixed. */
export function acknowledgeCopy(): string {
  return "Acknowledged. This will stop repeating from here — it isn't marked as fixed.";
}

function snoozeCopy(until: string): string {
  const formatted = new Date(`${until}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `Snoozed. It'll come back on ${formatted} if it hasn't resolved by then.`;
}

export function AlertCentre() {
  const queryClient = useQueryClient();
  const isReadOnly = useIsReadOnly();
  const [conflictMessage, setConflictMessage] = useState<string | undefined>();
  const [pendingId, setPendingId] = useState<string | undefined>();

  useEffect(() => {
    document.title = "Alerts — PropDesk";
  }, []);

  const listQuery = useQuery({
    queryKey: ALERT_INSTANCES_LIST_QUERY_KEY,
    queryFn: () => apiFetch<Page<AlertInstanceResource>>("/alert-instances"),
  });

  function removeFromCache(id: string) {
    queryClient.setQueryData<Page<AlertInstanceResource>>(ALERT_INSTANCES_LIST_QUERY_KEY, (existing) => {
      if (existing === undefined) {
        return existing;
      }
      return {
        data: existing.data.filter((row) => row.id !== id),
        meta: { ...existing.meta, total: Math.max(0, existing.meta.total - 1) },
      };
    });
  }

  /**
   * A concurrent-modification conflict (spec judgement call 2, VC-3.9-07):
   * shown clearly, above the list — never silently swallowed, and never
   * silently retried with a now-stale intent. The list is refetched so the
   * row this action could no longer apply to reflects its real, current
   * state rather than staying wrong in the cache.
   */
  async function handleConflict(error: ApiError) {
    setConflictMessage(error.message);
    await queryClient.invalidateQueries({ queryKey: ALERT_INSTANCES_LIST_QUERY_KEY });
  }

  async function handleAcknowledge(alert: AlertInstanceResource) {
    setPendingId(alert.id);
    setConflictMessage(undefined);
    try {
      await apiFetch(`/alert-instances/${alert.id}/acknowledge`, { method: "POST" });
      removeFromCache(alert.id);
      showToast(acknowledgeCopy());
    } catch (error) {
      if (error instanceof ApiError && (error.code === "conflict" || error.code === "invalid_transition")) {
        await handleConflict(error);
      } else if (error instanceof ApiError) {
        setConflictMessage(error.message);
      } else {
        throw error;
      }
    } finally {
      setPendingId(undefined);
    }
  }

  async function handleSnooze(alert: AlertInstanceResource, until: string) {
    setPendingId(alert.id);
    setConflictMessage(undefined);
    try {
      await apiFetch(`/alert-instances/${alert.id}/snooze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ until }),
      });
      removeFromCache(alert.id);
      showToast(snoozeCopy(until));
    } catch (error) {
      if (error instanceof ApiError && (error.code === "conflict" || error.code === "invalid_transition")) {
        await handleConflict(error);
      } else if (error instanceof ApiError) {
        setConflictMessage(error.message);
      } else {
        throw error;
      }
    } finally {
      setPendingId(undefined);
    }
  }

  const rows = listQuery.data?.data ?? [];
  const isEmpty = listQuery.data !== undefined && rows.length === 0;
  const errorMessage = listQuery.error instanceof ApiError ? listQuery.error.message : undefined;

  return (
    <div className="mx-auto max-w-content-max">
      <ReadOnlyNotice className="mb-4" />
      <h1 className="text-title font-semibold tracking-title text-ink md:hidden">Alerts</h1>
      <p className="mb-6 mt-2 text-body text-ink-secondary">Everything PropDesk currently thinks needs your attention.</p>

      {conflictMessage ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-border-hairline bg-warning-quiet px-4 py-3 text-body text-ink"
        >
          {conflictMessage}
        </div>
      ) : null}

      {listQuery.isPending ? (
        <p className="text-body text-ink-secondary">Loading&hellip;</p>
      ) : listQuery.isError ? (
        <p role="alert" className="text-body text-danger">
          {errorMessage ?? "Something went wrong. Please try again."}
        </p>
      ) : isEmpty ? (
        <div className="mx-auto max-w-empty-state-max py-12 text-center">
          <h2 className="text-heading font-semibold text-ink">{EMPTY_STATE_SENTENCE}</h2>
        </div>
      ) : (
        // Owner-requested (2026-08-29): the same table shell every other
        // list in the app already has (`DataTable`'s own `rounded-lg` +
        // `shadow-card`; `SectionCard.tsx`'s identical tinted-header
        // treatment for the dashboard's own "Needs attention now" panel) —
        // this screen was the one list still sitting on a plain hairline
        // border with no header bar at all.
        <div className="overflow-hidden rounded-lg bg-surface shadow-card">
          <div className="flex h-table-header items-center bg-vibrant-amber-quiet px-4 text-label font-medium text-ink-secondary">
            {rows.length === 1 ? "1 open alert" : `${rows.length} open alerts`}
          </div>
          <ul aria-label={`${rows.length === 1 ? "1 open alert" : `${rows.length} open alerts`}`}>
            {rows.map((alert) => (
              <AlertRow
                key={alert.id}
                alert={alert}
                onAcknowledge={handleAcknowledge}
                onSnooze={handleSnooze}
                isAcknowledging={pendingId === alert.id}
                isSnoozing={pendingId === alert.id}
                canMutate={!isReadOnly}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
